import { useState, useEffect } from 'react';
import styles from './ProductBasicInfo.module.scss';

const ProductBasicInfo = ({
  formData,
  errors,
  companies,
  warehouses,
  updateFormData,
  clearError
}) => {
  const [userWarehouseId, setUserWarehouseId] = useState(null);
  const [userWarehouseName, setUserWarehouseName] = useState('');

  // Get user's warehouse_id from localStorage on component mount
  useEffect(() => {
    const warehouseId = localStorage.getItem('warehouse_id');
    const userDataStr = localStorage.getItem('userData');

    if (warehouseId && warehouseId.trim() !== '' && warehouseId !== 'null' && warehouseId !== 'undefined') {
      setUserWarehouseId(warehouseId);
      console.log('User has warehouse_id from localStorage:', warehouseId);

      // If we have the warehouse list, find the warehouse name
      if (warehouses && warehouses.length > 0) {
        const warehouse = warehouses.find(w => w.id.toString() === warehouseId);
        if (warehouse) {
          setUserWarehouseName(warehouse.name);
          console.log('Found warehouse name:', warehouse.name);
        }
      }

      // Set warehouse_id in formData if not already set
      if (!formData.warehouse_id) {
        updateFormData('warehouse_id', warehouseId);
        console.log('Setting warehouse_id in formData:', warehouseId);
      }
    }

    // Try to get warehouse_id from userData if not in separate field
    if (!warehouseId && userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        if (userData.warehouse_id) {
          const warehouseIdStr = userData.warehouse_id.toString();
          setUserWarehouseId(warehouseIdStr);
          localStorage.setItem('warehouse_id', warehouseIdStr);

          if (!formData.warehouse_id) {
            updateFormData('warehouse_id', warehouseIdStr);
            console.log('Setting warehouse_id from userData:', warehouseIdStr);
          }
        }
      } catch (error) {
        console.error('Error parsing userData:', error);
      }
    }
  }, [warehouses, formData.warehouse_id, updateFormData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Prevent changing warehouse if user has assigned warehouse
    if (name === 'warehouse_id' && userWarehouseId) {
      return;
    }

    updateFormData(name, value);
    clearError(name);
  };

  const handleSelectFocus = (fieldName) => {
    clearError(fieldName);
  };

  // Helper function to determine select color
  const getSelectColor = (value) => {
    return value ? '#000000' : '#797775';
  };

  // Find user's warehouse from warehouses list
  const getUserWarehouse = () => {
    if (!userWarehouseId || !warehouses || warehouses.length === 0) return null;
    return warehouses.find(w => w.id.toString() === userWarehouseId.toString());
  };

  const userWarehouse = getUserWarehouse();

  // Check if user has a valid warehouse_id
  const hasAssignedWarehouse = userWarehouseId &&
    userWarehouseId.trim() !== '' &&
    userWarehouseId !== 'null' &&
    userWarehouseId !== 'undefined';

  return (
    <>
      {/* Product Type Selection */}
      <div className={`${styles.label_input}`}>
        <label>Product Type</label>
        <div className={styles.input_container}>
          <select
            name="productType"
            value={formData.productType}
            onChange={handleInputChange}
            onFocus={() => handleSelectFocus('productType')}
            className={styles.select_field}
            style={{ color: getSelectColor(formData.productType) }}
          >
            <option value="non-perishable">Non-Perishable</option>
            <option value="perishable">Perishable</option>
          </select>
        </div>
      </div>

      {/* Company Selection */}
      <div className={`${styles.label_input} ${errors.company_id ? styles.error : ''}`}>
        <label>Select Company<span>*</span></label>
        <div className={styles.input_container}>
          <select
            name="company_id"
            value={formData.company_id}
            onChange={handleInputChange}
            onFocus={() => handleSelectFocus('company_id')}
            className={styles.select_field}
            style={{ color: getSelectColor(formData.company_id) }}
          >
            <option value="" disabled hidden>Select Company</option>
            {companies.filter(company => company.status === 1).map(company => (
              <option key={company.id} value={company.id}>{company.company_name}</option>
            ))}
          </select>
          {errors.company_id && <span className={styles.errorMessage}>{errors.company_id}</span>}
        </div>
      </div>

      {/* Product Name */}
      <div className={`${styles.label_input} ${errors.product_name ? styles.error : ''}`}>
        <label>Product Name<span>*</span></label>
        <div className={styles.input_container}>
          <input
            type="text"
            name="product_name"
            value={formData.product_name}
            onChange={handleInputChange}
            onFocus={() => clearError('product_name')}
            placeholder="Enter Product Name"
          />
          {errors.product_name && <span className={styles.errorMessage}>{errors.product_name}</span>}
        </div>
      </div>

      {/* Warehouse Selection */}
      <div className={`${styles.label_input} ${errors.warehouse_id ? styles.error : ''}`}>
        <label>Select Warehouse<span>*</span></label>
        <div className={styles.input_container}>
          {hasAssignedWarehouse ? (
            // Show readonly display for users with assigned warehouse
            <div className={styles.readonly_warehouse_container}>
              <div className={styles.readonly_warehouse_field}>
                <div className={styles.warehouse_display}>
                  {userWarehouse ? (
                    <>
                      <span className={styles.warehouse_name}>
                        {userWarehouse.name}
                      </span>
                      {/* {userWarehouse.address && (
                        <span className={styles.warehouse_address}>
                          {userWarehouse.address}
                        </span>
                      )} */}
                    </>
                  ) : (
                    <span className={styles.warehouse_id_text}>
                      Warehouse ID: {userWarehouseId}
                    </span>
                  )}
                </div>
                <input
                  type="hidden"
                  name="warehouse_id"
                  value={userWarehouseId}
                  readOnly
                />
              </div>
              <div className={styles.warehouse_note}>
                <span className={styles.note_icon}>ℹ️</span>
                <span>Your assigned warehouse (cannot be changed)</span>
              </div>
            </div>
          ) : (
            // Show dropdown for users without assigned warehouse
            <select
              name="warehouse_id"
              className={styles.select_field}
              value={formData.warehouse_id}
              onChange={handleInputChange}
              onFocus={() => handleSelectFocus('warehouse_id')}
              style={{ color: getSelectColor(formData.warehouse_id) }}
            >
              <option value="" hidden>Select Warehouse</option>
              {warehouses.filter(warehouse => warehouse.status === 1).map(warehouse => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          )}
          {errors.warehouse_id && <span className={styles.errorMessage}>{errors.warehouse_id}</span>}
        </div>
      </div>
    </>
  );
};

export default ProductBasicInfo;